import React, { useState } from "react";
import { C } from "../lib/colors.js";
import { forecasterDisplayName, triggerLabel } from "../lib/forecast/engines.js";
import { fmtPct } from "../lib/forecast/formatPct.js";
import { ui } from "../lib/telemetry/instrument.js";

function DebugSection({ title, data, accentColor = "#33aa88" }) {
  const [expanded, setExpanded] = useState(false);
  const text =
    typeof data === "string"
      ? data
      : typeof data === "object"
        ? JSON.stringify(data, null, 2)
        : String(data || "");
  if (!text.trim()) return null;
  return (
    <div>
      <div
        {...ui("pipeline-debug.section", title.toLowerCase().replace(/\s+/g, "-"))}
        onClick={() => setExpanded((v) => !v)}
        style={{
          cursor: "pointer",
          fontSize: 11,
          color: accentColor,
          letterSpacing: 0.6,
          display: "flex",
          alignItems: "center",
          gap: 4,
          userSelect: "none",
        }}
      >
        <span style={{ fontSize: 12 }}>{expanded ? "▼" : "▶"}</span> {title}
      </div>
      {expanded && (
        <pre
          style={{
            fontSize: 11,
            color: "#aab",
            background: C.debugBg,
            border: `1px solid ${C.btnBg}`,
            borderRadius: 6,
            padding: 8,
            marginTop: 4,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            maxHeight: 300,
            overflowY: "auto",
            fontFamily: '"IBM Plex Mono",monospace',
          }}
        >
          {text}
        </pre>
      )}
    </div>
  );
}

function DebugPanel({ primary, comparison, primaryEngine, compareEngine }) {
  const renderSide = (forecast, label, color) => {
    const dbg = forecast?._debug;
    const trig = forecast?._obs?.postprocessModel;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ fontSize: 12, color, fontWeight: "bold" }}>{label}</div>
        {trig !== undefined && (
          <div style={{ fontSize: 11, color: "#888" }}>Trigger LLM: {triggerLabel(trig)}</div>
        )}
        {!dbg ? (
          <div style={{ color: "#556", fontSize: 12 }}>No debug data available</div>
        ) : (
          <>
            <DebugSection
              title="PREPROCESSED QUESTION"
              data={dbg.preprocessed_question}
              accentColor={color}
            />
            <DebugSection
              title="EVIDENCE"
              data={dbg.postprocess_inputs?.evidence}
              accentColor={color}
            />
            <DebugSection
              title="REASONING"
              data={dbg.postprocess_inputs?.reasoning}
              accentColor={color}
            />
            {dbg.postprocess_inputs?.assumption_block && (
              <DebugSection
                title="ASSUMPTION"
                data={dbg.postprocess_inputs.assumption_block}
                accentColor={color}
              />
            )}
            {dbg.raw_forecast_response && (
              <DebugSection
                title="RAW LLM RESPONSE"
                data={dbg.raw_forecast_response}
                accentColor={color}
              />
            )}
            <div style={{ fontSize: 11, color, marginTop: 4 }}>
              Events: {forecast?.events?.length || 0} · Prob:{" "}
              {fmtPct(forecast?.contract?.probability || 0)}%
            </div>
          </>
        )}
      </div>
    );
  };
  return (
    <div
      style={{
        padding: "8px 14px",
        display: "grid",
        gridTemplateColumns: comparison ? "1fr 1fr" : "1fr",
        gap: 16,
      }}
    >
      {renderSide(primary, forecasterDisplayName(primaryEngine), "#aa88ff")}
      {comparison && renderSide(comparison, forecasterDisplayName(compareEngine), "#33aa88")}
    </div>
  );
}

export { DebugSection, DebugPanel };
